/**
 * Финальный вариант универсального расчёта метрик продаж
 * -------------------------------------------------------
 * ✅ Всегда пересчитывает данные вручную из salesData.data
 * ✅ Игнорирует summary (т.к. iiko summary не всегда содержит полные итоги)
 * ✅ Корректно вычисляет все показатели:
 *    - общая сумма продаж, скидок, заказов, гостей
 *    - средний чек, средняя выручка с гостя
 *    - % скидки, % наценки
 *    - распределение по залу/доставке и по типам оплат
 */

import { isSelectionTotal, matchesSelection } from "../../../utils/restaurant";

export const calculateMetrics = (
  salesData,
  selectedPayType = "Все",
  selectedGroup = "Все"
) => {
  if (!salesData || typeof salesData !== "object") return getEmptyMetrics();

  // === 1️⃣ Берём исходные строки данных ===
  const data = Array.isArray(salesData.data) ? salesData.data : [];
  const summary = salesData?.summary;
  
  if (data.length === 0) return getEmptyMetrics();

  // === 2️⃣ Применяем фильтры (тип оплаты / подразделение) ===
  const filtered = data.filter((d) => {
    const payMatch =
      !selectedPayType ||
      selectedPayType === "Все" ||
      d.PayTypes === selectedPayType;
    const groupMatch = matchesSelection(d, selectedGroup);
    return payMatch && groupMatch;
  });

  const isFilterActive =
    (selectedPayType && selectedPayType !== "Все") ||
    (selectedGroup && selectedGroup !== "Все");

  const useData =
    filtered.length > 0
      ? filtered
      : isFilterActive
      ? []
      : data;

  // === 3️⃣ Агрегируем значения ===
  const totalSum = sumBy(useData, (d) => d?.DishDiscountSumInt || 0);
  const totalDiscount = sumBy(useData, (d) => d?.DiscountSum || 0);
  let totalOrders = sumBy(
    useData,
    (d) => d?.["UniqOrderId.OrdersCount"] || (d?.UniqOrderId?.OrdersCount ?? 0)
  );
  let totalGuests = sumBy(useData, (d) => d?.GuestNum || 0);

  // Если есть summary из iiko, берём итоги заказов/гостей именно оттуда,
  // чтобы избежать двойного учёта при нескольких типах оплат одного заказа.
  // Логика:
  // - Если фильтр по группе активен, ищем агрегат по этой группе в summary
  // - Если фильтра по группе нет, используем общий итог (пустой объект {})
  // - Если есть фильтр и по группе, и по типу оплаты, используем данные из data
  // (summary уже определён выше на строке 24)
  let summaryRow = null;
  
  if (selectedGroup && selectedGroup !== "Все") {
    // Фильтр по группе активен - ищем агрегат по группе
    summaryRow = getGroupSummaryRow(summary, selectedGroup);
  } else {
    // Фильтра по группе нет - используем общий итог
    summaryRow = getOverallSummaryRow(summary);
  }
  
  // Используем summary только если:
  // 1. summary найден (summaryRow не null)
  // 2. Нет фильтра по типу оплаты (т.к. в summary нет агрегатов по группе+тип оплаты одновременно)
  if (summaryRow && (!selectedPayType || selectedPayType === "Все")) {
    const sOrders = Number(summaryRow?.["UniqOrderId.OrdersCount"]) || 0;
    const sGuests = Number(summaryRow?.GuestNum) || 0;
    // Всегда перезаписываем значениями из summary (они корректны и без двойного учета)
    totalOrders = sOrders;
    totalGuests = sGuests;
  }
  const totalMarkup = sumBy(useData, (d) => d?.ProductCostBase?.Profit || 0);

  // === 4️⃣ Средние и проценты ===
  // Берём DiscountPercent из summary, если есть (совпадает с iiko «Итого»)
  let discountPercent = 0;
  if (summaryRow && summaryRow.DiscountPercent != null && (!selectedPayType || selectedPayType === "Все")) {
    const sVal = Number(summaryRow.DiscountPercent) || 0;
    discountPercent = sVal <= 1 ? sVal * 100 : sVal;
  } else {
    discountPercent =
      totalSum + totalDiscount > 0
        ? (totalDiscount / (totalSum + totalDiscount)) * 100
        : 0;
  }

  const avgOrder = totalOrders > 0 ? totalSum / totalOrders : 0;
  const avgPerGuest = totalGuests > 0 ? totalSum / totalGuests : 0;
  const markupPercent = totalSum > 0 ? (totalMarkup / totalSum) * 100 : 0;

  // === 5️⃣ Распределение Зал / Доставка ===
  // Используем Delivery.IsDelivery если доступно, иначе fallback на другие признаки
  const isDelivery = (d) => {
    const deliveryFlag = d?.["Delivery.IsDelivery"];
    if (deliveryFlag !== undefined && deliveryFlag !== null) {
      // iiko возвращает "DELIVERY_ORDER" для доставки и "ORDER_WITHOUT_DELIVERY" для зала
      const flagStr = String(deliveryFlag).toUpperCase();
      if (flagStr === "DELIVERY_ORDER") {
        return true;
      }
      if (flagStr === "ORDER_WITHOUT_DELIVERY") {
        return false;
      }
      // Поддержка других форматов (булевые, строки)
      return deliveryFlag === true || 
             deliveryFlag === "true" || 
             deliveryFlag === 1 ||
             String(deliveryFlag).toLowerCase() === "true" ||
             String(deliveryFlag).toLowerCase() === "доставка";
    }
    
    // Fallback 1: Проверяем OrderType
    if (d.OrderType && d.OrderType !== "Обычный заказ" && d.OrderType !== "") {
      return true;
    }
    
    // Fallback 2: Определяем доставку по типам оплаты (агрегаторы доставки)
    const payType = (d.PayTypes || "").toLowerCase();
    const deliveryPayTypes = [
      "яндекс.еда",
      "яндекс еда",
      "сбермаркет",
      "delivery",
      "доставка",
      "delivery club",
      "deliveryclub"
    ];
    
    if (deliveryPayTypes.some(type => payType.includes(type))) {
      return true;
    }
    
    return false;
  };

  const hallSum = sumBy(
    useData,
    (d) => !isDelivery(d) && (d.DishDiscountSumInt || 0)
  );

  const deliverySum = sumBy(
    useData,
    (d) => isDelivery(d) && (d.DishDiscountSumInt || 0)
  );

  const totalOrderTypeSum = hallSum + deliverySum;
  const hallPercent =
    totalOrderTypeSum > 0 ? (hallSum / totalOrderTypeSum) * 100 : 0;
  const deliveryPercent =
    totalOrderTypeSum > 0 ? (deliverySum / totalOrderTypeSum) * 100 : 0;

  // === 6️⃣ Распределение Налич / Безнал ===
  const cashSum = sumBy(
    useData,
    (d) =>
      d.PayTypes &&
      typeof d.PayTypes === "string" &&
      d.PayTypes.toLowerCase().includes("налич")
        ? d.DishDiscountSumInt || 0
        : 0
  );

  const nonCashSum = sumBy(
    useData,
    (d) =>
      d.PayTypes &&
      typeof d.PayTypes === "string" &&
      !d.PayTypes.toLowerCase().includes("налич")
        ? d.DishDiscountSumInt || 0
        : 0
  );

  const totalPaySum = cashSum + nonCashSum;
  const cashPercent = totalPaySum > 0 ? (cashSum / totalPaySum) * 100 : 0;
  const nonCashPercent = totalPaySum > 0 ? (nonCashSum / totalPaySum) * 100 : 0;

  // === 7️⃣ Полный разрез по типам оплат ===
  const payTypeToSum = new Map();
  const payTypeToDiscount = new Map();
  const payTypeToOrders = new Map(); // Мапа для подсчета заказов по типам оплат
  for (const d of useData) {
    const label = (d?.PayTypes || "Не указан").toString();
    const value = Number(d?.DishDiscountSumInt) || 0;
    const discount = Number(d?.DiscountSum) || 0;
    const ordersCount = Number(d?.["UniqOrderId.OrdersCount"] || d?.UniqOrderId?.OrdersCount || 0);
    
    if (!payTypeToSum.has(label)) {
      payTypeToSum.set(label, 0);
      payTypeToDiscount.set(label, 0);
      payTypeToOrders.set(label, 0);
    }
    payTypeToSum.set(label, payTypeToSum.get(label) + value);
    payTypeToDiscount.set(label, payTypeToDiscount.get(label) + discount);
    payTypeToOrders.set(label, payTypeToOrders.get(label) + ordersCount);
  }
  const allPayTypesBreakdown = Array.from(payTypeToSum.entries()).map(([label, value]) => ({ 
    label, 
    value,
    discount: payTypeToDiscount.get(label) || 0,
    ordersCount: payTypeToOrders.get(label) || 0
  }));

  // === 8️⃣ Источники заказа доставки ===
  // Для карточки "Зал / Доставка" - источники заказов доставки
  const deliveryOriginToSum = new Map();
  const deliveryOriginToOrders = new Map();
  
  for (const d of useData) {
    // Только источники для доставки
    if (isDelivery(d)) {
      const originName = (d?.OriginName || "Не указан").toString();
      const value = Number(d?.DishDiscountSumInt) || 0;
      const ordersCount = Number(d?.["UniqOrderId.OrdersCount"] || d?.UniqOrderId?.OrdersCount || 0);
      
      if (!deliveryOriginToSum.has(originName)) {
        deliveryOriginToSum.set(originName, 0);
        deliveryOriginToOrders.set(originName, 0);
      }
      deliveryOriginToSum.set(originName, deliveryOriginToSum.get(originName) + value);
      deliveryOriginToOrders.set(originName, deliveryOriginToOrders.get(originName) + ordersCount);
    }
  }
  
  const deliveryOriginsBreakdown = Array.from(deliveryOriginToSum.entries())
    .map(([label, value]) => ({
      label,
      value,
      ordersCount: deliveryOriginToOrders.get(label) || 0
    }))
    .sort((a, b) => (b.value || 0) - (a.value || 0));

  // === 9️⃣ Возвращаем итог ===
  return {
    totalSum,
    totalDiscount,
    totalOrders,
    totalGuests,
    avgOrder,
    avgPerGuest,
    totalMarkup,
    markupPercent,
    discountPercent,
    hallPercent,
    deliveryPercent,
    cashPercent,
    nonCashPercent,
    orderTypeBreakdown: [
      { label: "Зал", value: hallSum },
      { label: "Доставка", value: deliverySum },
    ],
    payTypeBreakdown: [
      { label: "Наличные", value: cashSum },
      { label: "Безнал", value: nonCashSum, legendLabel: "Безналичные" },
    ],
    allPayTypesBreakdown,
    deliveryOriginsBreakdown,
  };
};

// 🔹 Вспомогательная функция для суммирования
function sumBy(array, fn) {
  return Array.isArray(array)
    ? array.reduce((acc, el) => {
        const val = fn(el);
        return acc + (typeof val === 'number' && !isNaN(val) ? val : 0);
      }, 0)
    : 0;
}

// 🔹 Заглушка на случай пустых данных
function getEmptyMetrics() {
  return {
    totalSum: 0,
    totalDiscount: 0,
    totalOrders: 0,
    totalGuests: 0,
    avgOrder: 0,
    avgPerGuest: 0,
    totalMarkup: 0,
    markupPercent: 0,
    discountPercent: 0,
    hallPercent: 0,
    deliveryPercent: 0,
    cashPercent: 0,
    nonCashPercent: 0,
    orderTypeBreakdown: [
      { label: "Зал", value: 0 },
      { label: "Доставка", value: 0 },
    ],
    payTypeBreakdown: [
      { label: "Наличные", value: 0 },
      { label: "Безналичные", value: 0 },
    ],
    allPayTypesBreakdown: [],
    deliveryOriginsBreakdown: [],
  };
}

// 🔹 Пытаемся извлечь строку «Итого» из summary iiko.
// В ответе buildSummary summary может быть массивом массивов вида: [groupObj, metricsObj].
// Искомая строка «Итого» имеет пустой groupObj ({}). Возвращаем metricsObj.
function getOverallSummaryRow(summary) {
  if (!Array.isArray(summary)) return null;
  for (const row of summary) {
    if (Array.isArray(row) && row.length === 2) {
      const groupObj = row[0] || {};
      const metricsObj = row[1] || null;
      if (groupObj && typeof groupObj === "object" && Object.keys(groupObj).length === 0) {
        return metricsObj;
      }
    } else if (row && typeof row === "object") {
      // На всякий случай поддержим плоский вариант summary
      return row;
    }
  }
  return null;
}

// 🔹 Извлекаем агрегат по выбранному ресторану (или группе в нём) из summary iiko:
// строка ровно этого уровня, без разбивки по типу оплаты и доставке (см. isSelectionTotal).
function getGroupSummaryRow(summary, selectedGroup) {
  if (!Array.isArray(summary) || !selectedGroup) return null;
  
  for (const row of summary) {
    if (Array.isArray(row) && row.length === 2) {
      const groupObj = row[0] || {};
      const metricsObj = row[1] || null;
      
      if (isSelectionTotal(groupObj, selectedGroup)) {
        return metricsObj;
      }
    }
  }
  return null;
}
